import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import {
  BRAND_PLANS_PATH,
  BRAND_SERVICES_PATH,
  ROUTES,
  clientDetailPath,
} from "../../../routes/paths";
import PageHeader from "../../../components/layout/PageHeader";
import Pagination from "../../../components/ui/Pagination";
import {
  AddAction,
  Button,
  EmptyStateProps,
  MobileSearchField,
  Tabs,
  TabItem,
  failedEmptyState,
  searchEmptyState,
  useIsMobile,
  useLoadPhase,
} from "../../../components/ui";
// The one screen-sized illustration on this page, and only for the workspace
// that has never added a client — see `EmptyState`'s `welcome` variant.
import { ReactComponent as FirstClientMark } from "../../../assets/svg/No data images/my client.svg";
import ClientCards from "../components/ClientCards";
import ClientsTable from "../components/ClientsTable";
import { toClientRow } from "../clientMapping";
import { countClientNews } from "../clientNews";
import { clientsNewsSince } from "../openedClients";
import { useClientLists } from "../hooks/useClients";
import { useOpenedClients } from "../hooks/useOpenedClients";
import { useWaitingSubmissions } from "../hooks/useWaitingSubmissions";
// A deep import rather than the billing barrel: that barrel pulls in the
// checkout and result screens, and this page only needs to know whether the
// workspace is allowed to add anything.
import { useSubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
// Deep imports for the same reason: the brand barrel pulls in the whole My
// brand screen, and this page only needs to know whether the workspace has
// said what it sells and what it does, and how to say so if it has not.
import PlansRequiredModal from "../../brand/components/PlansRequiredModal";
import ServicesRequiredModal from "../../brand/components/ServicesRequiredModal";
import { usePlansCreated } from "../../brand/hooks/usePlansCreated";
import { useServicesCreated } from "../../brand/hooks/useServicesCreated";
import { useSetupGate } from "../../brand/hooks/useSetupGate";
import { ClientTabId } from "../types/clients.types";

/** How many rows a page of the table holds, from `md` up. */
const CLIENTS_PER_PAGE = 10;

/**
 * How many cards a phone lays out at a time.
 *
 * There are no pages on a phone — walking a roster there is scrolling — so a
 * batch is how much of the list is built into the page at once. The next batch
 * is laid out while there is still a screenful above it, so the list goes on
 * rather than stopping to be continued. Same figure the revenue screen uses.
 */
const CARDS_PER_BATCH = 20;

/**
 * The tabs in the order they are drawn, and what each one calls itself.
 *
 * Active first because it is the list a dietitian opens this screen for, then
 * New — the clients on the books who have not bought anything yet, and so the
 * ones with something outstanding to do about them — then Old, and Renewal
 * last. Renewal sits at the end because it is the one tab that is not a
 * slice of the roster: its clients are on the Active tab as well, with their
 * plans a few days from running out. See `LIST_STATUS_OF_TAB`.
 */
const TAB_ORDER: ClientTabId[] = ["active", "new", "old", "renewal"];

const TAB_COPY: Record<ClientTabId, { label: string; cardTitle: string }> = {
  active: { label: "Active", cardTitle: "Total active clients" },
  new: { label: "New", cardTitle: "Total new clients" },
  old: { label: "Old clients", cardTitle: "Total old clients" },
  renewal: { label: "Renewal", cardTitle: "Total clients up for renewal" },
};

/**
 * The tabs that between them hold every client exactly once.
 *
 * Renewal is left out: it repeats clients the Active tab already has, so an
 * empty Renewal tab says nothing about whether the workspace has anybody, and
 * counting it towards "this workspace is empty" would be counting the same
 * rows twice.
 */
const PARTITION_TABS: ClientTabId[] = ["active", "new", "old"];

/** An unknown or missing `?tab=` opens on Active, rather than on nothing. */
const tabFromParam = (value: string | null): ClientTabId =>
  TAB_ORDER.includes(value as ClientTabId) ? (value as ClientTabId) : "active";

const MyClientsPage: React.FC = () => {
  const navigate = useNavigate();

  // In the URL rather than in state, the way the client detail screen holds
  // its own tabs, so the tab survives a refresh and can be linked to — which
  // is how the dashboard's "clients renewal" figure lands on Renewal rather
  // than on the roster. See `CLIENTS_RENEWAL_PATH`.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = tabFromParam(searchParams.get("tab"));
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  // How many cards a phone has laid out. It only ever grows, and it grows
  // ahead of the reader — see `ClientCards`.
  const [shown, setShown] = useState(CARDS_PER_BATCH);

  /**
   * Which of the two shapes this screen takes.
   *
   * Not a `md:` class: a table of five columns and a column of cards are not
   * the same markup at two widths, and building both would put every client in
   * the accessibility tree twice. So only one of them is ever rendered — see
   * `ClientCards`.
   */
  const isMobile = useIsMobile();

  // Three lists, one request each — the backend's `?status=` is what decides
  // which tab a client lands in now. See `useClientLists`.
  const { lists, reload } = useClientLists();
  const { clients, status } = lists[activeTab];

  // Which rows are carrying a flag, so a tab can say it is holding one without
  // being opened. The same two readings the rows themselves draw — a client
  // who has landed unopened, and answers that have come back unread — counted
  // per list. See `clientNews`.
  const seen = useOpenedClients();
  const since = clientsNewsSince();
  const waiting = useWaitingSubmissions();

  // Clients are one of the three things a plan is what pays for, so with no
  // plan running both ways to Add client are off. The roster itself stays
  // readable. See `useSubscriptionGate`.
  const gate = useSubscriptionGate();

  // And a client is added holding one of the workspace's own plans and working
  // towards one of its services, so a workspace short of either has nothing to
  // add them against: the Plan field or the Goal field would open on an empty
  // list. Both ways to Add client go through the prompt instead — see
  // `useSetupGate`.
  //
  // `status === "ready"` is load-bearing on both: a read still out, or one that
  // failed, leaves the door open rather than standing this in front of somebody
  // who has been selling for a year.
  const { plansCreated, status: plansStatus } = usePlansCreated();
  const { servicesCreated, status: servicesStatus } = useServicesCreated();
  const noPlans = plansStatus === "ready" && !plansCreated;
  const noServices = servicesStatus === "ready" && !servicesCreated;
  const setup = useSetupGate(noPlans || noServices);

  const addClient = setup.guard(() => navigate(ROUTES.addClient));

  // A list that lands quickly goes straight from blank to filled: the table's
  // placeholders are only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(status === "loading");

  // No splitting left to do: the open tab's list is the one the server was
  // asked for. This used to be a filter on `archived_at` over a single
  // unfiltered read, which could only ever tell two lists apart — a client who
  // had never bought anything was filed among the active ones.
  const source = useMemo(() => clients.map(toClientRow), [clients]);

  // Searching and paging still happen here rather than on the server: the
  // screen counts each tab and searches inside one, so it holds the list it is
  // showing whole. Both move server-side the day the counts come with it.
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return source;
    return source.filter(
      (client) =>
        client.name.toLowerCase().includes(query) ||
        // Any one of them matching is a hit: a client working towards three
        // things should be found by whichever of the three was typed.
        client.goals.some((goal) => goal.toLowerCase().includes(query))
    );
  }, [source, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / CLIENTS_PER_PAGE));
  // Clamp rather than reset: a filter that shrinks the list past the current
  // page should land on the last page, not throw away the user's position.
  const currentPage = Math.min(page, totalPages);

  // A page of the table, or as much of the roster as the phone has laid out.
  const visible = isMobile
    ? filtered.slice(0, shown)
    : filtered.slice(
        (currentPage - 1) * CLIENTS_PER_PAGE,
        currentPage * CLIENTS_PER_PAGE
      );

  /** Whether the phone is holding back clients it already has. */
  const hasMore = shown < filtered.length;

  /** The next batch of cards. Safe to call twice for the same one — the list
   *  asks as its end comes into view, which can happen on consecutive scroll
   *  events. */
  const showMore = () =>
    setShown((current) => Math.min(current + CARDS_PER_BATCH, filtered.length));

  // The tabs name the lists; how many are in one is the open list's own
  // business, and the card heading below says it. Four numbers across the
  // strip made the row read as a sum rather than as four places to go.
  const tabs: TabItem<ClientTabId>[] = TAB_ORDER.map((tab) => ({
    id: tab,
    label: TAB_COPY[tab].label,
    // On any tab holding something unlooked-at, not just New: a client who has
    // been on a plan for a month and has just sent their pre-consultation
    // answers back is news, and their row is under Active. A tab still loading
    // says nothing, for the same reason it shows no count.
    dot:
      lists[tab].status === "ready" &&
      countClientNews(lists[tab].clients, seen, since, waiting) > 0,
  }));

  // A workspace that has never added anybody, as opposed to one tab that
  // happens to be empty — all three of the lists that split the roster have
  // to have answered to know that. See `PARTITION_TABS`.
  const rosterEmpty = PARTITION_TABS.every(
    (tab) => lists[tab].status === "ready" && lists[tab].clients.length === 0
  );

  /** A different list is read from the top — whichever of the two ways this
   *  screen is being read. */
  const handleTabChange = (tab: ClientTabId) => {
    // Replaced rather than pushed: Back should leave the roster, not walk back
    // through every tab that was looked at along the way.
    setSearchParams({ tab }, { replace: true });
    setSearch("");
    setPage(1);
    setShown(CARDS_PER_BATCH);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
    setShown(CARDS_PER_BATCH);
  };

  /** What the table shows in place of rows, whichever way it came to have none.
   *  The ways are genuinely different and get genuinely different answers: a
   *  failed fetch, a search that was too narrow, a workspace with no clients in
   *  it at all, and a single tab that is empty while the other two have rows.
   *
   *  Nothing about loading here: while the list is out the table shimmers a
   *  page of rows, and the status line above is what says so in words. */
  const emptyState = (): EmptyStateProps => {
    if (status === "failed")
      return failedEmptyState(
        "We could not load your clients",
        <Button size="sm" variant="secondary" onClick={reload}>
          Try again
        </Button>
      );

    if (search.trim()) return searchEmptyState("clients");

    // One empty tab out of three is a list reporting a result, not a workspace
    // with nothing in it — so it says where the clients actually are rather
    // than inviting anyone to add a first one. An empty list of clients is
    // exactly what a nutritionist would fear seeing, and each of these says
    // plainly that nothing has been lost.
    if (!rosterEmpty && activeTab !== "active") {
      const backToActive = (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => handleTabChange("active")}
        >
          Go to active clients
        </Button>
      );

      if (activeTab === "old")
        return {
          title: "No old clients yet",
          description:
            "Clients whose plans have ended collect here on their own. Nothing has been archived yet.",
          action: backToActive,
        };

      // Nothing outstanding rather than nothing here: this tab is empty every
      // time everybody's plan has a while left to run, which is the good
      // state and should not read as one.
      if (activeTab === "renewal")
        return {
          title: "No renewals coming up",
          description:
            "Clients whose plans are about to run out show up here, so you can reach them before they lapse. Nobody is close yet.",
          action: backToActive,
        };

      return {
        title: "No new clients right now",
        description:
          "A client you have added but not yet sold a plan to waits here. Everyone on your books is on one.",
        action: backToActive,
      };
    }

    // The Active tab empty while the others have rows: nobody is on a running
    // plan, which is a state worth naming rather than papering over with the
    // first-run welcome.
    if (!rosterEmpty)
      return {
        title: "No active clients right now",
        description:
          "Nobody has a plan running at the moment. Your other clients are under the New and Old clients tabs.",
      };

    // Nothing in any of the three lists is a workspace on its first day rather
    // than a list reporting a result, so this one is the welcome layout: what
    // the screen is for, then the one thing to do about it.
    return {
      variant: "welcome",
      // Sized here rather than left at the file's own 60px: the two welcome
      // marks in the panel are drawn at different sizes, and the class is what
      // makes this one and the enquiries one match on screen.
      illustration: <FirstClientMark aria-hidden className="w-11 h-11" />,
      title: "Add your first client",
      description: "Create your client list to get started.",
      highlights: {
        title: "Your clients, organised",
        items: [
          "Keep every client profile organised in one place.",
          "Create & share personalised meal plans.",
          "Automatic renewal reminders for you and your clients.",
        ],
      },
      action: (
        <Button
          size="md"
          startIcon={<Plus className="w-4 h-4" />}
          onClick={addClient}
          {...gate.props("add a client")}
        >
          Add client
        </Button>
      ),
    };
  };

  /**
   * The way to add a client, built once and set in one of two places.
   *
   * Beside the title on a phone, where the header row is the only one with
   * width to spare: sharing the tab strip's row there left the three tabs
   * scrolling inside whatever the button did not want, which is the one row on
   * the screen that has to stay readable. From `md` up there is room for both,
   * and the button keeps the place it has always had.
   */
  const addButton = (
    <AddAction
      label="Add client"
      phoneLabel="New"
      className="flex-shrink-0"
      onClick={addClient}
      {...gate.props("add a client")}
    />
  );

  return (
    // The page owns the viewport height instead of growing past it, so the
    // only thing that scrolls is the table body inside the card.
    <div className="min-h-full md:h-full flex flex-col md:overflow-hidden p-page pt-page-top max-md:pb-0">
      <PageHeader title="My clients" actions={isMobile ? addButton : undefined} />

      {/* `min-w-0` on the tab strip is what lets it scroll instead of pushing
          the button off the row — see `Tabs`. The button keeps its width at
          every size: it is the one thing on this screen that is always worth
          the space. On a phone it is not on this row at all, so the three tabs
          have the full width to lay themselves out in. */}
      <div className="mt-4 sm:mt-5 flex-shrink-0 flex items-end justify-between gap-3 sm:gap-4 border-b border-line">
        <div className="min-w-0">
          <Tabs tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />
        </div>
        {/* Held off the divider by the same 12px the tab labels clear it by —
            `Tabs` spends that as `pb-3` under each label, and the button, being
            the other thing bottom-aligned in this row, had nothing and sat on
            the line. The margin only grows the row upwards: the tabs stay
            bottom-anchored, so the underline under the active one does not
            move. */}
        {!isMobile && <div className="mb-3 flex-shrink-0">{addButton}</div>}
      </div>

      {/* One card holding the lot from `md` up. On a phone there is no card:
          the rows are cards of their own, and a border around a column of
          bordered cards is a box inside a box that costs a phone's width twice
          over. So the section is only a column there, and the clients sit on
          the page's own background — see `ClientCards`. Closer under the tabs
          on a phone, the way Revenue's is: the search is the next thing on
          the same list, not a new section. */}
      <section
        className={`mt-4 md:mt-5 flex-1 min-h-0 flex flex-col overflow-hidden ${
          isMobile ? "" : "rounded-md border border-line bg-white"
        }`}
      >
        {/* A line of its own at the full width of the screen on a phone — the
            same field the plans list searches with, see `MobileSearchField`.
            The card's title goes with the card: the tab above already says
            which list this is, and on a phone it is three words higher up the
            same screen. */}
        {isMobile ? (
          <div className="flex-shrink-0 pb-3">
            <MobileSearchField
              value={search}
              onChange={handleSearchChange}
              placeholder="Search clients"
            />
          </div>
        ) : (
          <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-6">
            <h2 className="flex items-center gap-1.5 text-[18px] 3xl:text-[20px] font-medium text-[#111C2D]">
              <span>{TAB_COPY[activeTab].cardTitle}</span>
              {/* Not until the list is in: a "0" against a heading that is
                  still loading reads as an answer rather than as a blank. The
                  whole list, not the page or the search — the heading says
                  "total". */}
              {status === "ready" && <span>({source.length})</span>}
            </h2>

            <div className="relative w-full sm:w-[260px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
              <input
                type="search"
                value={search}
                onChange={(event) => handleSearchChange(event.target.value)}
                placeholder="Search"
                aria-label="Search clients"
                className="w-full rounded-md border border-line py-2 pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-subtle focus:outline-none focus:border-brand"
              />
            </div>
          </div>
        )}

        {/* Said as soon as the request goes out, whether or not anything is
            drawn for it: this line is not seen, so it costs nothing to be
            early, and the skeleton rows it stands for are hidden from screen
            readers — they are the shape of an answer rather than the answer. */}
        {status === "loading" && (
          <p role="status" className="sr-only">
            Loading your clients…
          </p>
        )}

        {/* Rendered rather than hidden: a table and a column of cards holding
            the same clients would be the same rows twice in the accessibility
            tree, and the cards inside the hidden half would still be reachable
            by keyboard.

            The phone has no pages under it — walking the roster there is
            scrolling, and the batches are laid out from the rows already in
            hand. The table keeps its pagination.

            Activity is the running plan's length, and an old client has no
            plan running — that tab drops it rather than show a list of dashes.
            The New tab keeps it: a client who has just bought their first plan
            has a length to report, and it is what the tab being "same as
            Active" means. */}
        {isMobile ? (
          <ClientCards
            clients={visible}
            phase={phase}
            showActivity={activeTab !== "old"}
            onSelectClient={(client) => navigate(clientDetailPath(client.id))}
            empty={emptyState()}
            hasMore={hasMore}
            onEndReached={showMore}
          />
        ) : (
          <>
            <ClientsTable
              clients={visible}
              phase={phase}
              showActivity={activeTab !== "old"}
              onSelectClient={(client) => navigate(clientDetailPath(client.id))}
              empty={emptyState()}
            />

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setPage}
              resultCount={visible.length}
            />
          </>
        )}
      </section>

      {/* Not a screen of its own: the roster behind it is perfectly readable,
          and the person may well have come here to look at it rather than to
          add anybody. "Not now" leaves them on it.

          The plan is asked for first where both are missing, so a workspace on
          its first day is walked through them in the order the My brand tabs
          hold them rather than shown two dialogs at once. */}
      {setup.prompting &&
        (noPlans ? (
          <PlansRequiredModal
            onCreatePlan={() => navigate(BRAND_PLANS_PATH)}
            onDismiss={setup.dismiss}
          />
        ) : (
          <ServicesRequiredModal
            onCreateService={() => navigate(BRAND_SERVICES_PATH)}
            onDismiss={setup.dismiss}
          />
        ))}
    </div>
  );
};

export default MyClientsPage;
